import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDataServiceIDs,
  getDataServiceVariants,
  getElectricityServiceIDs,
  getTVServiceIDs,
  getTVServiceVariants,
  initiateVtuPaystackPayment,
  verifyMeterNumber,
  verifyVtuPaystackPayment,
  getInsuranceVariants,
  getInsuranceVehicleColors,
  getInsuranceEngineCapacities,
  getInsuranceStates,
  getInsuranceLGAs,
  getInsuranceVehicleBrands,
  getInsuranceVehicleModels,
  verifyJambProfile,
} from "../../vtu/services";
import {
  errorNotifier,
  successNotifier,
} from "../../../components/NotificationHandler";
import {
  BILL_ITEMS,
  INITIAL_FORM_VALUES,
  NETWORK_SERVICE_ID,
  PAYLOAD_LABELS,
} from "./payBillModal/constants";
import { PayBillFormTabs } from "./payBillModal/PayBillFormTabs";
import { PayBillResultContent } from "./payBillModal/PayBillResultContent";
import {
  clearReturnParams,
  clearStoredPendingPayment,
  extractVariations,
  extractInsuranceColors,
  extractInsuranceBrands,
  extractInsuranceStates,
  extractInsuranceCapacities,
  extractInsuranceModels,
  extractInsuranceLGAs,
  formatCurrency,
  formatItemLabel,
  getStoredPendingPayment,
  getVariationAmount,
  getVariationCode,
  getVtuPaymentErrorMessage,
  setStoredPendingPayment,
} from "./payBillModal/utils";

const PENDING_RETRY_WAIT_SECONDS = 90;

const PayBillModal = ({
  isOpen,
  onClose,
  defaultItem = "data",
  size = "lg",
}) => {
  const queryClient = useQueryClient();
  const hasAutoVerifiedReturnRef = useRef(false);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [resultState, setResultState] = useState(null);
  const [submittedPayload, setSubmittedPayload] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [nowTimestamp, setNowTimestamp] = useState(Date.now());
  const [jambProfileName, setJambProfileName] = useState(null);

  const selectedTabIndex = useMemo(() => {
    const index = BILL_ITEMS.findIndex((item) => item === currentItem);
    return index < 0 ? 0 : index;
  }, [currentItem]);

  const {
    data: serviceIDs,
    isLoading: serviceIDsLoading,
    error: serviceIDsError,
  } = useQuery({
    queryFn: getDataServiceIDs,
    queryKey: ["data-service-ids"],
    enabled: isOpen,
  });

  const { data: tvServiceIDs } = useQuery({
    queryFn: getTVServiceIDs,
    queryKey: ["tv-service-ids"],
    enabled: isOpen,
  });

  const { data: electricityServiceIDs } = useQuery({
    queryFn: getElectricityServiceIDs,
    queryKey: ["electricity-service-ids"],
    enabled: isOpen,
  });

  const { data: dataVariantResponse } = useQuery({
    queryKey: ["data-service-variants", formValues.data.network],
    queryFn: () => getDataServiceVariants(formValues.data.network),
    enabled: Boolean(isOpen && formValues.data.network),
  });

  const { data: tvVariantResponse } = useQuery({
    queryKey: ["tv-service-variants", formValues.cable.cableTv],
    queryFn: () => getTVServiceVariants(formValues.cable.cableTv),
    enabled: Boolean(isOpen && formValues.cable.cableTv),
  });

  const { data: insuranceVariantResponse } = useQuery({
    queryKey: ["insurance-variants"],
    queryFn: getInsuranceVariants,
    enabled: isOpen,
  });

  const { data: vehicleColorsResponse } = useQuery({
    queryKey: ["insurance-vehicle-colors"],
    queryFn: getInsuranceVehicleColors,
    enabled: isOpen,
  });

  const { data: engineCapacitiesResponse } = useQuery({
    queryKey: ["insurance-engine-capacities"],
    queryFn: getInsuranceEngineCapacities,
    enabled: isOpen,
  });

  const { data: insuranceStatesResponse } = useQuery({
    queryKey: ["insurance-states"],
    queryFn: getInsuranceStates,
    enabled: isOpen,
  });

  const { data: insuranceLGAsResponse } = useQuery({
    queryKey: ["insurance-lgas", formValues.insurance.state],
    queryFn: () => getInsuranceLGAs(formValues.insurance.state),
    enabled: Boolean(isOpen && formValues.insurance.state),
  });

  const { data: vehicleBrandsResponse } = useQuery({
    queryKey: ["insurance-vehicle-brands"],
    queryFn: getInsuranceVehicleBrands,
    enabled: isOpen,
  });

  const { data: vehicleModelsResponse } = useQuery({
    queryKey: ["insurance-vehicle-models", formValues.insurance.vehicleMake],
    queryFn: () => getInsuranceVehicleModels(formValues.insurance.vehicleMake),
    enabled: Boolean(isOpen && formValues.insurance.vehicleMake),
  });

  const { data: educationVariantResponse } = useQuery({
    queryKey: [
      "education-service-variants",
      formValues.education.educationService,
    ],
    queryFn: () =>
      getDataServiceVariants(formValues.education.educationService),
    enabled: Boolean(isOpen && formValues.education.educationService),
  });

  const dataVariants = useMemo(
    () => extractVariations(dataVariantResponse),
    [dataVariantResponse],
  );

  const tvVariants = useMemo(
    () => extractVariations(tvVariantResponse),
    [tvVariantResponse],
  );

  const insuranceVariants = useMemo(
    () => extractVariations(insuranceVariantResponse),
    [insuranceVariantResponse],
  );

  const vehicleColors = useMemo(
    () => extractInsuranceColors(vehicleColorsResponse),
    [vehicleColorsResponse],
  );

  const engineCapacities = useMemo(
    () => extractInsuranceCapacities(engineCapacitiesResponse),
    [engineCapacitiesResponse],
  );

  const insuranceStates = useMemo(
    () => extractInsuranceStates(insuranceStatesResponse),
    [insuranceStatesResponse],
  );

  const insuranceLGAs = useMemo(
    () => extractInsuranceLGAs(insuranceLGAsResponse),
    [insuranceLGAsResponse],
  );

  const vehicleBrands = useMemo(
    () => extractInsuranceBrands(vehicleBrandsResponse),
    [vehicleBrandsResponse],
  );

  const vehicleModels = useMemo(
    () => extractInsuranceModels(vehicleModelsResponse),
    [vehicleModelsResponse],
  );

  const educationVariants = useMemo(
    () => extractVariations(educationVariantResponse),
    [educationVariantResponse],
  );

  const initiatePaymentMutation = useMutation({
    mutationFn: initiateVtuPaystackPayment,
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: verifyVtuPaystackPayment,
  });

  const verifyMeterMutation = useMutation({
    mutationFn: verifyMeterNumber,
  });

  const verifyJambProfileMutation = useMutation({
    mutationFn: verifyJambProfile,
  });

  const storedPending = getStoredPendingPayment();
  const isExternalVerifyPending =
    verifyPaymentMutation.isPending ||
    Boolean(pendingPayment?.verifying) ||
    Boolean(storedPending?.verifying);

  useEffect(() => {
    if (isOpen) {
      setResultState(null);
      setSubmittedPayload(null);
      setFormValues(INITIAL_FORM_VALUES);
      setPendingPayment(null);
      setCurrentItem(defaultItem || "data");

      const params = new URLSearchParams(window.location.search);
      if (params.get("vtuPayReturn") === "1") {
        const pending = getStoredPendingPayment();
        if (pending?.item) {
          setCurrentItem(pending.item);
        }
        if (pending?.reference && pending?.transactionId) {
          const normalizedPending = {
            ...pending,
            retryAfter:
              Number(pending.retryAfter) ||
              Date.now() + PENDING_RETRY_WAIT_SECONDS * 1000,
          };
          setPendingPayment(normalizedPending);
          setStoredPendingPayment(normalizedPending);
          setResultState("pending");
        }
      }
    }
  }, [defaultItem, isOpen]);

  useEffect(() => {
    if (!pendingPayment) return undefined;

    const intervalId = window.setInterval(() => {
      setNowTimestamp(Date.now());

      try {
        const stored = getStoredPendingPayment();

        // If the pending payment was cleared externally (background verify succeeded),
        // close the pending state and show success.
        if (!stored) {
          setPendingPayment(null);
          setResultState("success");
          return;
        }

        // If stored pending changed (for example verifying flag set by background verifier),
        // update local pending copy and ensure UI shows pending.
        const localSerialized = JSON.stringify(pendingPayment);
        const storedSerialized = JSON.stringify(stored);
        if (localSerialized !== storedSerialized) {
          setPendingPayment(stored);
          if (stored.verifying) setResultState("pending");
        }
      } catch (e) {
        // ignore storage read errors
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [pendingPayment]);

  useEffect(() => {
    if (!isOpen) {
      const params = new URLSearchParams(window.location.search);
      if (!params.get("vtuPayReturn")) {
        resetModalState();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      hasAutoVerifiedReturnRef.current = false;
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasPaystackReturn = params.get("vtuPayReturn") === "1";

    if (!hasPaystackReturn) {
      hasAutoVerifiedReturnRef.current = false;
      return;
    }

    if (hasAutoVerifiedReturnRef.current) {
      return;
    }

    hasAutoVerifiedReturnRef.current = true;

    const pending = getStoredPendingPayment();

    if (!pending?.reference || !pending?.transactionId) {
      errorNotifier("Missing payment details for verification");
      clearReturnParams();
      return;
    }

    if (pending?.verifying) {
      setPendingPayment(pending);
      setResultState("pending");
      return;
    }

    const verifyReturnedPayment = async () => {
      try {
        await verifyPaymentMutation.mutateAsync({
          reference: pending.reference,
          transactionId: pending.transactionId,
        });

        clearStoredPendingPayment();
        clearReturnParams();
        queryClient.invalidateQueries({ queryKey: ["vtu-transactions"] });
        successNotifier("VTU payment successful");
        setPendingPayment(null);
        setResultState("success");
      } catch (error) {
        console.error("VTU verify error:", error?.response ?? error);
        errorNotifier(getVtuPaymentErrorMessage(error));
        setPendingPayment(pending);
        setResultState("error");
      }
    };

    verifyReturnedPayment();
  }, [isOpen, queryClient, verifyPaymentMutation.mutateAsync]);

  const resetModalState = () => {
    setResultState(null);
    setSubmittedPayload(null);
    setPendingPayment(null);
    setFormValues(INITIAL_FORM_VALUES);
    setCurrentItem(defaultItem || "data");
    setJambProfileName(null);
    // clear persisted pending payment and any return params so a fresh flow starts
    try {
      clearStoredPendingPayment();
      clearReturnParams();
    } catch (e) {
      // ignore
    }
  };

  const closeModal = () => {
    resetModalState();
    onClose();
  };

  const handleTabChange = (index) => {
    const selectedItem = BILL_ITEMS[index] || "data";
    setCurrentItem(selectedItem);
    setResultState(null);
  };

  const handleInputChange = (item, field, value) => {
    setFormValues((previous) => {
      const updated = {
        ...previous,
        [item]: {
          ...previous[item],
          [field]: value,
        },
      };
      // Reset dependent fields on key selection changes
      if (item === "insurance" && field === "vehicleMake") {
        updated.insurance.vehicleModel = "";
      }
      if (item === "insurance" && field === "state") {
        updated.insurance.lga = "";
      }
      return updated;
    });
    if (item === "education" && (field === "billersCode" || field === "plan")) {
      setJambProfileName(null);
    }
  };

  const hasMissingRequiredFields = (item) => {
    if (item === "education") {
      const p = formValues.education;
      if (!p.educationService || !p.plan || !p.phone) return true;
      if (p.educationService === "jamb" && !p.billersCode) return true;
      return false;
    }
    const payload = formValues[item] || {};
    return Object.values(payload).some((value) => !String(value ?? "").trim());
  };

  const getCurrentPayload = () => formValues[currentItem] || {};

  const buildInsurancePayload = (payload) => {
    const selectedPlan = insuranceVariants.find(
      (variation) =>
        String(getVariationCode(variation)) === String(payload.insurancePlan),
    );
    const amount = getVariationAmount(selectedPlan);

    return {
      serviceType: "INSURANCE",
      amountPaid: amount,
      vtuPayload: {
        serviceID: "ui-insure",
        variation_code: Number(payload.insurancePlan),
        phone: payload.phone,
        Insured_Name: payload.insuredName,
        engine_capacity: Number(payload.engineCapacity),
        Chasis_Number: payload.chassisNumber,
        Plate_Number: payload.plateNumber,
        billersCode: payload.plateNumber,
        vehicle_make: Number(payload.vehicleMake),
        vehicle_color: Number(payload.vehicleColor),
        vehicle_model: Number(payload.vehicleModel),
        YearofMake: Number(payload.yearOfMake),
        state: Number(payload.state),
        lga: Number(payload.lga),
        email: payload.email,
        amount,
      },
    };
  };

  const buildEducationPayload = (payload) => {
    const selectedPlan = educationVariants.find(
      (v) => getVariationCode(v) === payload.plan,
    );
    const amount = getVariationAmount(selectedPlan);
    const vtuPayload = {
      serviceID: payload.educationService,
      variation_code: payload.plan,
      phone: payload.phone,
      quantity: Number(payload.quantity) || 1,
      amount,
    };
    if (payload.educationService === "jamb") {
      vtuPayload.billersCode = payload.billersCode;
    }
    return {
      serviceType: "EDUCATION",
      amountPaid: amount,
      vtuPayload,
    };
  };

  const buildInitiatePayload = (item, payload) => {
    if (item === "education") {
      return buildEducationPayload(payload);
    }
    if (item === "insurance") {
      return buildInsurancePayload(payload);
    }

    if (item === "airtime") {
      const amount = Number(payload.amount);
      return {
        serviceType: "AIRTIME",
        amountPaid: amount,
        vtuPayload: {
          serviceID: NETWORK_SERVICE_ID[payload.network] || payload.network,
          phone: payload.phoneNumber,
          amount,
        },
      };
    }

    if (item === "data") {
      const selectedDataPlan = dataVariants.find(
        (variation) => getVariationCode(variation) === payload.dataPlan,
      );
      const amount = getVariationAmount(selectedDataPlan);

      return {
        serviceType: "DATA",
        amountPaid: amount,
        vtuPayload: {
          serviceID: payload.network,
          phone: payload.phoneNumber,
          billersCode: payload.phoneNumber,
          variation_code: payload.dataPlan,
          amount,
        },
      };
    }

    if (item === "electricity") {
      const amount = Number(payload.amount);
      return {
        serviceType: "ELECTRICITY",
        amountPaid: amount,
        vtuPayload: {
          serviceID: payload.serviceProvider,
          billersCode: payload.meterNumber,
          type: payload.type,
          variation_code: payload.type,
          phone: payload.meterNumber,
          amount,
        },
      };
    }

    const selectedTvPlan = tvVariants.find(
      (variation) => getVariationCode(variation) === payload.cablePlan,
    );
    const amount = getVariationAmount(selectedTvPlan);

    return {
      serviceType: "TV",
      amountPaid: amount,
      vtuPayload: {
        serviceID: payload.cableTv,
        billersCode: payload.decoderNumber,
        variation_code: payload.cablePlan,
        subscription_type: "renewal",
        quantity: 1,
        amount,
      },
    };
  };

  const submitCurrentForm = async () => {
    const payload = getCurrentPayload();

    if (hasMissingRequiredFields(currentItem)) {
      setSubmittedPayload({ item: currentItem, ...payload });
      setResultState("error");
      return;
    }

    let verifiedJambName = null;
    try {
      if (currentItem === "electricity") {
        await verifyMeterMutation.mutateAsync({
          serviceID: payload.serviceProvider,
          billersCode: payload.meterNumber,
          type: payload.type,
        });
      }

      if (currentItem === "education" && payload.educationService === "jamb") {
        const verifyResult = await verifyJambProfileMutation.mutateAsync({
          billersCode: payload.billersCode,
          variation_code: payload.plan,
        });
        verifiedJambName =
          verifyResult?.content?.Customer_Name || verifyResult?.Customer_Name;
        if (verifiedJambName) setJambProfileName(verifiedJambName);
      }

      const initiatePayload = buildInitiatePayload(currentItem, payload);

      if (
        !initiatePayload?.amountPaid ||
        Number(initiatePayload?.amountPaid) <= 0
      ) {
        throw new Error("Unable to determine bill amount for this purchase");
      }

      const paymentData = await initiatePaymentMutation.mutateAsync({
        ...initiatePayload,
      });

      const nextPendingPayment = {
        ...paymentData,
        item: currentItem,
        retryAfter: Date.now() + PENDING_RETRY_WAIT_SECONDS * 1000,
      };

      setPendingPayment(nextPendingPayment);
      setStoredPendingPayment(nextPendingPayment);
      setSubmittedPayload({
        item: currentItem,
        ...payload,
        ...(verifiedJambName ? { jambProfileName: verifiedJambName } : {}),
      });
      setResultState("pending");
    } catch (error) {
      errorNotifier(
        error?.response?.data?.data ||
          error?.message ||
          "Failed to initiate payment",
      );
      setResultState("error");
    }
  };

  const verifyPayment = async () => {
    if (!pendingPayment?.reference || !pendingPayment?.transactionId) {
      errorNotifier("Missing payment details for verification");
      return;
    }

    try {
      await verifyPaymentMutation.mutateAsync({
        reference: pendingPayment.reference,
        transactionId: pendingPayment.transactionId,
      });

      clearStoredPendingPayment();
      clearReturnParams();
      queryClient.invalidateQueries({ queryKey: ["vtu-transactions"] });
      successNotifier("VTU payment successful");
      setPendingPayment(null);
      setResultState("success");
    } catch (error) {
      console.error("VTU verify error:", error?.response ?? error);
      errorNotifier(getVtuPaymentErrorMessage(error));
      setResultState("error");
    }
  };

  const proceedToCheckout = () => {
    if (!pendingPayment?.authorization_url) {
      errorNotifier("Checkout link is unavailable. Please try again.");
      return;
    }

    window.location.assign(pendingPayment.authorization_url);
  };

  const getSubmitDisabled = (item) =>
    (Boolean(pendingPayment) && retryCountdownSeconds > 0) ||
    hasMissingRequiredFields(item) ||
    verifyMeterMutation.isPending ||
    (item === "data" && (serviceIDsLoading || Boolean(serviceIDsError))) ||
    (item === "data" &&
      Boolean(formValues.data.network) &&
      dataVariants.length === 0) ||
    (item === "cable" &&
      Boolean(formValues.cable.cableTv) &&
      tvVariants.length === 0) ||
    (item === "insurance" && insuranceVariants.length === 0) ||
    (item === "education" &&
      Boolean(formValues.education.educationService) &&
      educationVariants.length === 0);

  const formFieldsProps = {
    formValues,
    handleInputChange,
    serviceIDs,
    electricityServiceIDs,
    tvServiceIDs,
    dataVariants,
    tvVariants,
    insuranceVariants,
    vehicleColors,
    engineCapacities,
    insuranceStates,
    insuranceLGAs,
    vehicleBrands,
    vehicleModels,
    educationVariants,
    jambProfileName,
  };

  const retryCountdownSeconds = Math.max(
    0,
    Math.ceil((Number(pendingPayment?.retryAfter || 0) - nowTimestamp) / 1000),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size={size}
      blockScrollOnMount={false}
      isCentered
      onCloseComplete={resetModalState}
      closeOnOverlayClick={!verifyPaymentMutation.isPending}
      closeOnEsc={!verifyPaymentMutation.isPending}
    >
      <ModalOverlay />
      <ModalContent
        maxHeight={"90vh"}
        overflowY={"auto"}
        overflowX={"hidden"}
        mx={"1em"}
        borderRadius="12px"
      >
        <Box
          bgGradient="linear(to right, #f6f6fa 30%, white 35%, rgb(136, 175, 218) 50%, primary.500 80%)"
          px="20px"
          py="25px"
        >
          <Text color="primary.500" fontWeight="700" fontSize="lg">
            Purchase {formatItemLabel(currentItem)}
          </Text>
        </Box>
        {!verifyPaymentMutation.isPending && (
          <ModalCloseButton color="white" />
        )}
        <ModalBody py="16px">
          {resultState ? (
            <PayBillResultContent
              resultState={resultState}
              currentItem={currentItem}
              pendingPayment={pendingPayment}
              isInitiatePending={initiatePaymentMutation?.isPending}
              isVerifyPending={isExternalVerifyPending}
              payloadLabels={PAYLOAD_LABELS}
              submittedPayload={submittedPayload}
              formatCurrency={formatCurrency}
              onBack={() => setResultState(null)}
              onProceedToCheckout={proceedToCheckout}
              onVerifyPayment={verifyPayment}
              onConfirm={closeModal}
            />
          ) : (
            <PayBillFormTabs
              billItems={BILL_ITEMS}
              selectedTabIndex={selectedTabIndex}
              handleTabChange={handleTabChange}
              submitCurrentForm={submitCurrentForm}
              pendingPayment={pendingPayment}
              retryCountdownSeconds={retryCountdownSeconds}
              onVerifyPending={verifyPayment}
              onProceedToCheckout={proceedToCheckout}
              isVerifyPending={isExternalVerifyPending}
              isSubmitLoading={
                initiatePaymentMutation.isPending ||
                verifyMeterMutation.isPending
              }
              getSubmitDisabled={getSubmitDisabled}
              formFieldsProps={formFieldsProps}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PayBillModal;
